import React, { FC, useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import get from 'lodash/get';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import Button from '@mui/material/Button';
import { Dialog, DialogActions, DialogContent, Link, Checkbox } from '@mui/material';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import CheckIcon from '@mui/icons-material/Check';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';

import { theme } from 'theme';
import { DialogTitleWithClose } from 'ui';
import { Loader } from 'ui/Loader/Loader';

import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { downloadService, uploadPortfolioUnified } from 'services';
import { portfolioUploadSchema, creditReviewCreatePortfolioSchema } from 'shared/validations';
import { useNotifications } from 'shared/hooks';
import { useFetchPortfolios } from '../../GraphQL/portfolios/hooks';

const useStyles = makeStyles({
  downloadTemplateContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  downloadButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  input: {
    display: 'none',
  },
  uploadContainer: {
    padding: '2px 4px',
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
  },
  fileUploadText: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  summaryContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  checkIcon: {
    fontSize: 80,
    fill: '#00d3a7',
  },
  strong: {
    fontWeight: 700,
  },
  summarySideNotes: {
    textAlign: 'left',
    fontSize: '0.75rem',
  },
  finishSideNotes: {
    textAlign: 'center',
    fontSize: '0.75rem',
    padding: '20px 50px',
    marginTop: '20px',
    borderTop: 'solid gray 1px',
    borderBottom: 'solid gray 1px',
  },
});

type SelectedPortfolio = {
  id?: string | null;
  name?: string;
  description?: string;
};

type PortfolioUploadResponseMissingItem = {
  type: string;
  value: string;
};

type PortfolioUploadModalProps = {
  open: boolean;
  onClose: () => void;
  onUploaded?: (refresh: boolean, reason: string) => void;
  destinationPortfolioId?: string;
};

export const steps = {
  UPLOAD: 1,
  CREATE_PORTFOLIO: 2,
  SUMMARY: 3,
  FINISH: 4,
};

export const OldPortfolioUploadModal: FC<PortfolioUploadModalProps> = ({
  open,
  onClose,
  onUploaded,
  destinationPortfolioId = null,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { notifyError } = useNotifications();
  const navigate = useNavigate();

  const sampleTemplateUrl = '/user-profile/portfolios/upload-portfolio';

  const [fileUploadInputRef, setFileUploadInputRef] = useState<HTMLInputElement>();

  const [isPending, setIsPending] = useState(false);

  const [error, setError] = useState<any>();

  const [fileData, setFileData] = useState({ fileUploadText: '' });

  const [step, setStep] = useState(steps.UPLOAD);
  const [summaryStep, setSummaryStep] = useState(0);

  const [uploadEntities, setUploadEntities] = useState(false);

  const [selectedPortfolio, setSelectedPortfolio] = useState<SelectedPortfolio>({});

  const [isCreatePortfolioOpen, setIsCreatePortfolioOpen] = useState(false);

  /**
   * create portfolio modal handlers
   */
  const handleClickCreateNew = () => {
    setIsCreatePortfolioOpen(true);
    refetch();
  };

  const handleCreatePortfolioClose = () => {
    setIsCreatePortfolioOpen(false);
  };

  const handlePortfolioCreateDone = (createdPortfolioId: string, createdPortfolioName: string) => {
    setSelectedPortfolio({ id: createdPortfolioId, name: createdPortfolioName });
  };

  useEffect(() => {
    if (isCreatePortfolioOpen) {
      handleAddToSelectedPortfolioClick();
      setIsCreatePortfolioOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio]);

  /**
   * prepare portfolios list
   */
  const [portfoliosStart, setPortfoliosStart] = useState(0);
  const [portfoliosLimit, setPortfoliosLimit] = useState(1);

  const {
    data: portfoliosData,
    loading: isPortfoliosLoading,
    refetch,
  } = useFetchPortfolios(portfoliosStart, portfoliosLimit);

  const { portfolios } = portfoliosData || { total: 0, portfolios: [] };

  /**
   *
   */
  useEffect(() => {
    if (error) {
      notifyError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  /**
   * finish / close modal
   */
  const closeModal = () => {
    setSelectedPortfolio({});
    setStep(steps.UPLOAD);
    setSummaryStep(0);
    onClose();
  };

  const handleDone = async () => {
    closeModal();
    if (!destinationPortfolioId) {
      navigate('/portfolios');
    }
  };

  /**
   * download portfolio template
   */
  const downloadErrorHandler = () => {
    notifyError(t('PORTFOLIO_UPLOAD.FAILED_DOWNLOAD_PORTFOLIO_TEMPLATE'));
  };

  const handleDownloadTemplateClick = () =>
    downloadService({
      path: sampleTemplateUrl,
      name: 'portfolio-template.xlsx',
      onError: downloadErrorHandler,
    });

  /**
   * handle next/previous steps
   */
  const previousStep = () => {
    switch (step) {
      case steps.UPLOAD:
        break;
      case steps.SUMMARY:
        if (summaryStep > 0) {
          setSummaryStep(summaryStep - 1);
        } else {
          setStep(step - 1);
        }
        break;
      default:
        setStep(step - 1);
    }
  };

  const fileUploadInputRefChanged = (element: HTMLInputElement) => setFileUploadInputRef(element);

  // eslint-disable-next-line consistent-return
  const handleSubmit = async (payload?: any, newPortfolioId?: string | undefined) => {
    if (payload) setFileData(payload);
    if (!destinationPortfolioId && !newPortfolioId) {
      return setStep(steps.CREATE_PORTFOLIO);
    }
    const dataToUpload = payload ?? fileData;

    setIsPending(true);

    uploadPortfolioUnified({
      portfolio: dataToUpload && dataToUpload.fileUploadInput,
      uploadEntities,
      portfolioId: destinationPortfolioId ?? newPortfolioId,
    })
      .then(() => {
        setStep(steps.FINISH);
        if (onUploaded) {
          onUploaded(true, refreshReasons.UPDATED);
        }
      })
      .catch(() => {
        setError('Unexpected error while uploading portfolio.');
        closeModal();
      })
      .finally(() => setIsPending(false));
  };

  useEffect(() => {
    if (step === steps.CREATE_PORTFOLIO) {
      setPortfoliosStart(0);
      setPortfoliosLimit(20);
    }
  }, [step]);

  const onSelectPortfolio = (e: FormEvent, defaultAction: (e: FormEvent) => any) => {
    const target = e.target as HTMLInputElement;
    setSelectedPortfolio({ id: target.value });
    if (defaultAction) {
      defaultAction(e);
    }
  };

  const handleAddToSelectedPortfolioClick = () => {
    if (selectedPortfolio?.id) {
      handleSubmit(null, selectedPortfolio.id);
    }
  };

  const updateUploadBoxData = (
    setFieldValueFunc: (name: string, value: any) => void,
    file: any | null,
    text: string,
  ) => {
    if (!file && fileUploadInputRef) {
      fileUploadInputRef.value = '';
    }
    setFieldValueFunc('fileUploadInput', file);
    setFieldValueFunc('fileUploadText', text);
  };

  const stepUploadInitialValues = {
    description: '',
    origin: window.location.href,
    fileUploadText: '',
    fileUploadInput: null,
  };

  const stepCreatePortfolioInitialValues = {
    portfolioId: '',
  };

  /**
   * upload step
   */
  const stepUpload = (
    <Formik
      key={steps.UPLOAD}
      initialValues={stepUploadInitialValues}
      onSubmit={(payload) => handleSubmit(payload)}
      validationSchema={portfolioUploadSchema(['csv', 'xls', 'xlsx'])}
    >
      {({ values, errors, touched, setFieldValue }) => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>{t('PORTFOLIO_UPLOAD.INFO_1')}</Typography>
              </Box>
              <Box mb={2}>
                <Typography>{t('PORTFOLIO_UPLOAD.INFO_2')}</Typography>
              </Box>
              <Divider />
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography>{`${t('UPLOAD.FILE_FORMAT')}: .csv, .xls, .xlsx`}</Typography>
                  <Typography>{`${t('UPLOAD.MAX_SIZE')}: 100 ${t(
                    'ENTITIES',
                  ).toLowerCase()}`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={handleDownloadTemplateClick}
                    variant="outlined"
                    disabled={isPending}
                  >
                    {t('DOWNLOAD_TEMPLATE')}
                  </Button>
                </Box>
              </div>
              <Divider />
              <ErrorMessage name="fileUploadInput">
                {(message) => (
                  <Box mt={2}>
                    <Typography color="error">{message}</Typography>
                  </Box>
                )}
              </ErrorMessage>
              <Paper className={classes.uploadContainer}>
                <input
                  className={classes.input}
                  id="file-upload"
                  name="file-upload"
                  ref={fileUploadInputRefChanged}
                  type="file"
                  onChange={(event: FormEvent) => {
                    updateUploadBoxData(
                      setFieldValue,
                      get((event.currentTarget as HTMLInputElement)?.files, '[0]', null),
                      get((event.currentTarget as HTMLInputElement)?.files, '[0].name', ''),
                    );
                  }}
                />
                <Field name="fileUploadText">
                  {({ field }: any) => (
                    <InputBase
                      {...field}
                      className={classes.fileUploadText}
                      placeholder={t('UPLOAD.NO_FILE_SELECTED')}
                      error={errors.fileUploadInput && touched.fileUploadText}
                      readOnly
                    />
                  )}
                </Field>
                <IconButton
                  type="submit"
                  disabled={!fileUploadInputRef || !fileUploadInputRef.value}
                  onClick={() => {
                    updateUploadBoxData(setFieldValue, null, '');
                  }}
                >
                  <DeleteIcon />
                </IconButton>
                <label htmlFor="file-upload">
                  <Button variant="contained" color="primary" component="span">
                    {t('UPLOAD.BROWSE')}
                  </Button>
                </label>
              </Paper>

              <Box mt={2}>
                <Grid container alignItems="center">
                  <Grid item>
                    <Checkbox
                      name="uploadEntities"
                      defaultChecked={false}
                      onChange={(event) => setUploadEntities(event.target.checked)}
                    />
                  </Grid>
                  <Grid item>
                    <Typography>{t('PORTFOLIO_UPLOAD.ADD_INSTRUMENTS')}</Typography>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              {step !== steps.FINISH && step !== steps.UPLOAD && (
                <Box>
                  <Button variant="outlined" onClick={previousStep}>
                    {t('PREVIOUS')}
                  </Button>
                </Box>
              )}
              <div style={{ flex: '1 0 0' }} />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || isPortfoliosLoading || !get(values, 'fileUploadInput')}
              >
                {t('NEXT')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  /**
   * create portfolio step
   */
  const stepCreatePortfolio = (
    <Formik
      key={steps.CREATE_PORTFOLIO}
      initialValues={stepCreatePortfolioInitialValues}
      onSubmit={(payload) => handleSubmit(payload)}
      validationSchema={creditReviewCreatePortfolioSchema}
    >
      {() => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>{t('UPLOAD.UPLOAD_SELECTED')}</Typography>
              </Box>
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography color="primary">{`${
                    fileData && fileData.fileUploadText
                  }`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button onClick={() => setStep(steps.UPLOAD)} variant="contained" color="primary">
                    {t('UPLOAD.CHANGE_FILE')}
                  </Button>
                </Box>
              </div>
              <Box mt={2}>
                <Typography>{t('PORTFOLIO_UPLOAD.CREATE_PORTFOLIO')}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {!!portfolios && (
                  <Field name="portfolioId">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        select
                        label={t('PORTFOLIO_UPLOAD.SELECT_PORTFOLIO')}
                        fullWidth
                        onChange={(e) => onSelectPortfolio(e, field.onChange)}
                      >
                        {portfolios
                          .filter((item: any) => !item.isDedicated)
                          .map(({ id, name }) => (
                            <MenuItem key={id} value={id}>
                              {name}
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                  </Field>
                )}
              </Box>
              <Divider />
            </DialogContent>
            <DialogActions>
              <Box>
                <Button onClick={previousStep}>{t('PREVIOUS')}</Button>
              </Box>
              <div style={{ flex: '1 0 0' }} />
              <Button variant="outlined" onClick={handleClickCreateNew}>
                {t('Create new portfolio')}
              </Button>
              <Button
                onClick={handleAddToSelectedPortfolioClick}
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || isPortfoliosLoading}
              >
                {t('NEXT')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  /**
   * Last step
   */
  const stepFinish = (
    <>
      <DialogContent className={classes.textCenter}>
        <Box
          width={100}
          height={100}
          borderRadius="50%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginY={2}
          marginX="auto"
          boxShadow={theme.customShadows[1]}
        >
          <CheckIcon className={classes.checkIcon} />
        </Box>
        <Typography className={classes.textCenter}>
          {t('PORTFOLIO_UPLOAD.UPLOADING_PORTFOLIO')}
        </Typography>
        <Typography className={classes.finishSideNotes}>
          {t('PORTFOLIO_UPLOAD.YOU_WILL_BE_NOTIFIED')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          disabled={isPending || isPortfoliosLoading}
          onClick={handleDone}
        >
          {t('NEXT')}
        </Button>
      </DialogActions>
    </>
  );

  const stepsMapping = {
    [steps.UPLOAD]: stepUpload,
    [steps.CREATE_PORTFOLIO]: stepCreatePortfolio,
    [steps.FINISH]: stepFinish,
  };

  return (
    <>
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitleWithClose onClose={closeModal}>
          {t('PORTFOLIO_UPLOAD.TITLE')}
        </DialogTitleWithClose>
        {(isPending || isPortfoliosLoading) && <Loader cover />}
        {stepsMapping[step]}
      </Dialog>

      <CreateNewPortfolioModal
        open={isCreatePortfolioOpen}
        onClose={handleCreatePortfolioClose}
        onCreate={handlePortfolioCreateDone}
      />
    </>
  );
};
